<template>
	<div :class="listWithSearch && 'h-full'">
		<div class="relative @container" :class="listWithSearch && 'h-full flex flex-col'">
			<div class="flex justify-between items-center gap-8 mb-4">
				<slot v-if="!hideHeader" name="header">
					<div>
						<div class="flex items-center gap-3">
							<h3 class="font-secondary text-heading-primary typo-h3">
								Vyberte vaší pobočku
							</h3>
						</div>
						<p v-if="warning" class="typo-body-s text-body-secondary mb-4 mt-2">
							{{ warning }}
						</p>
					</div>
				</slot>
				<div v-if="showCategoryFilter" class="ml-auto max-md:hidden">
					<div class="bg-neutral-300 p-1 rounded-[5rem] flex gap-2" aria-label="Filtrování poboček">
						<button
							v-for="(item, index) in hypermarketTypeCategories"
							:key="index"
							type="button"
							class="btn pr-3 pl-2 text-body-s border border-border-tertiary transition-all py-[.3125rem] gap-1 text-sm hover:border-border-active hover:bg-white"
							:class="item.value === hypermarketActiveType ? 'font-bold border-border-active bg-white cursor-default' : 'border-transparent bg-transparent font-normal text-neutral-900'"
							:aria-selected="item.value === hypermarketActiveType"
							@click="hypermarketActiveType = item.value"
						>
							<NuxtIcon
								:name="item.icon"
								class="text-icon-primary text-sm"
								filled
								aria-hidden="true"
							/>
							{{ item.label }}
						</button>
					</div>
				</div>
			</div>
			<FormKit
				v-if="listWithSearch"
				preserve
				type="form"
				:actions="false"
				role="search"
				aria-label="Vyhledávání pobočky"
				@submit.prevent
			>
				<FormKit
					v-model="searchFormModel"
					type="search"
					label="Hledat pobočku"
					placeholder="Například Brno"
					:maxlength="100"
					:classes="{
						outer: 'flex flex-auto mt-2 mb-3.5',
						wrapper: 'flex flex-auto',
						inner: 'bg-background-secondary xl:border-border-primary w-full !max-w-none !px-3 xl:rounded-lg',
						input: '!py-[0.5rem] !px-2.5 flex-auto !text-base xl:!py-[.725rem]',
						label: 'sr-only'
					}"
				>
					<template #prefixIcon>
						<NuxtIcon name="search" filled class="text-icon-primary text-[1.375rem] xl:text-[1.1rem] shrink-0" />
					</template>
				</FormKit>
			</FormKit>
			<template v-if="hasLoadedBranches">
				<div v-if="hasVisibleBranchOptions && showLinks" class="max-md:hidden" :class="listWithSearch && 'overflow-auto overscroll-contain'">
					<div class="grid @banner:grid-cols-[1fr_auto]">
						<section
							v-for="section in visibleSections"
							:key="section.value"
							class="space-y-3 mt-3"
						>
							<h4 class="typo-body-s font-normal text-neutral-800 flex items-center gap-2 px-3.5">
								<NuxtIcon
									:name="section.icon"
									filled
									aria-hidden="true"
								/>
								{{ section.label }}

								<button v-if="section.info" type="button" class="ml-auto @banner:ml-10">
									<Tooltip bubble-class="!max-w-[15rem] text-xs" right-arrow>
										<template #default>
											<span class="text-xs text-body-highlight underline underline-offset-1 flex items-center gap-1">
												{{ section.info.title }}
												<NuxtIcon name="info" filled class="text-body-highlight cursor-help" />
											</span>
										</template>
										<template #tooltip>
											{{ section.info.tooltip }}
										</template>
									</Tooltip>
								</button>
							</h4>
							<div
								class="flex shrink min-h-0 items-stretch w-full md:clear-both md:w-auto"
								:class="listWithSearch
									? 'flex-col'
									: [
										'max-h-full overscroll-contain overflow-auto grid grid-cols-1 md:grid-flow-col md:auto-cols-fr',
										hypermarketActiveType === 'all' ? 'sm:grid-rows-8 lg:grid-rows-6' : 'sm:grid-rows-6 lg:grid-rows-4',
									]"
							>
								<template v-for="item in section.options" :key="item.value">
									<AbsoluteInternalLink
										v-if="!item.disabled"
										:to="getHypermarketLink(item.value)"
										class="flex items-center py-3 px-3.5 font-bold leading-tight underline-offset-2 gap-2 group/option focus-visible:bg-background-noticeable active:bg-background-noticeable focus-visible:outline-hidden focus:z-1 rounded-full text-body-primary"
										@click="changeHypermarket(item.value)"
									>
										<NuxtIcon
											name="chevron-right"
											class="text-icon-primary"
											filled
										/>
										<span class="group-hover/option:underline">
											{{ item.label }}
										</span>
										<div
											v-if="item.value === selectedHypermarket"
											class="text-brand-globus-green-dark bg-functional-success-secondary font-normal px-2 py-1 rounded-full typo-body-caption no-underline"
										>
											Vybraná
										</div>
									</AbsoluteInternalLink>
									<div
										v-else
										class="flex items-center py-3 px-3.5 font-bold leading-tight underline-offset-2 gap-2 rounded-full text-body-quaternary cursor-not-allowed"
									>
										<NuxtIcon
											name="chevron-right"
											class="text-icon-primary"
											filled
										/>
										<div class="flex flex-col gap-0.5">
											<span class="text-base">
												{{ item.label }}
											</span>
											<span v-if="item.disabledText" class="text-xs font-normal">
												{{ item.disabledText }}
											</span>
										</div>
									</div>
								</template>
							</div>
						</section>
					</div>
				</div>
				<BreakpointRender :breakpoint="isDesktopLayout">
					<template #true>
						<div v-if="hasVisibleBranchOptions && !showLinks" class="space-y-6">
							<PartialsHypermarketSelectorList
								v-for="section in visibleSections"
								:key="section.value"
								v-model:selected="selectedOption"
								:options="section.options"
								:show-search="false"
								wide-variant
							>
								<template #legend>
									{{ section.label }}
								</template>
							</PartialsHypermarketSelectorList>
						</div>
					</template>
					<template #false>
						<FormKit
							v-if="hasVisibleBranchOptions"
							v-model="selectedOption"
							type="selectbox"
							:options="flatDisplayedResults"
							:placeholder="dropdownPlaceholder"
							:classes="{
								outer: 'max-w-[32.5rem] mx-auto md:my-6',
								inner: 'border border-border-tertiary !rounded-30xl max-w-none',
								input: 'py-[0.375rem] !bg-transparent pl-4 pr-1.5 typo-body-caption text-body-tertiary',
								label: 'text-body-s text-body-secondary',
							}"
							label="Pobočky"
							@node="handleSameOption"
						>
							<template #prefixCustomIcon>
								<NuxtIcon
									name="map-pin"
									class="text-icon-primary text-2xl transition duration-300"
									filled
								/>
							</template>
							<template #selectIcon="context">
								<button
									class="w-8 h-8 flex items-center justify-center"
								>
									<NuxtIcon
										name="chevron-right"
										class="text-icon-primary text-2xl transition duration-300"
										:class="{'rotate-90': context.state.open}"
										filled
									/>
								</button>
							</template>
						</FormKit>
					</template>
				</BreakpointRender>
				<div v-if="!hasVisibleBranchOptions" class="grow shrink overflow-auto custom-scrollbar border-t border-t-border-secondary mt-2 pt-4 flex flex-col items-center justify-center text-center px-4">
					<NuxtIcon name="frown" class="text-icon-primary rem:text-[48px]" filled />
					<p class="typo-body-s text-body-secondary mt-4">
						Nenašli jsme žádné pobočky, které by odpovídaly vašemu vyhledávání.
					</p>
				</div>
			</template>
			<template v-else>
				<NuxtIcon
					name="custom/loader"
					class="text-icon-primary p-2 text-2xl block [&_svg]:animate-spin mb-2"
					filled
				/>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {localeIncludes} from 'locale-includes';
	import {useGtm} from '@gtm-support/vue-gtm';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useShoppingCartStore} from '~/stores/shoppingCart';
	import type {Option as SelectboxOption} from '~/types/formkitSelectbox';

	type Props = {
		showLinks?: boolean;
		hideHeader?: boolean;
		hideHeaderIcon?: boolean;
		hideCategoryFilter?: boolean;
		dropdownDefaultHm?: boolean;
		redirectToBranch?: boolean;
		listWithSearch?: boolean;
		forceDesktopLayout?: boolean;
	};

	type Emits = {
		(e: 'update:hm'): void;
	};

	type BranchOption = {
		label: string;
		value: string;
		disabled?: boolean;
		disabledText?: string;
	};

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const route = useRoute();
	const gtm = useGtm();
	const breakpoints = useTailwindBreakpoints();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const hypermarketActiveType = ref<string>('all');
	const hypermarketTypeCategories = ref([
		{
			value: 'all',
			label: 'Všechny',
			icon: 'globe',
		},
		{
			value: 'hypermarkets',
			label: 'Hypermarkety',
			icon: 'custom/company',
		},
		{
			value: 'fresh-stores',
			label: 'Prodejny Fresh',
			icon: 'custom/house',
		},
	]);

	const branchSpecificPath = useBranchSpecificPath();
	const {requirement: branchSelectorRequirement} = useBranchSelectorAvailabilityRequirement();

	const {selectedHypermarket, groupedBranches, hypermarkets, freshBranches, selectedHypermarketInstance, selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const {visitedHypermarketInstance} = useVisitedHypermarket();

	const mdAndLarger = breakpoints.greaterOrEqual('md');
	const isDesktopLayout = computed(() => props.forceDesktopLayout || mdAndLarger.value);
	const availabilityBranchSlugs = computed(() => branchSelectorRequirement.value
		? (groupedBranches.value.hypermarkets ?? [])
			.concat(groupedBranches.value.fresh ?? [])
			.map((branch) => branch.slug)
		: [],
	);
	const {data: branchesPageAvailability} = useBranchesPageAvailability(availabilityBranchSlugs);

	function createBranchOption(label: string, value: string): BranchOption {
		const requirement = branchSelectorRequirement.value;
		const isDisabled = requirement
			? !branchesPageAvailability.value[value]?.[requirement.key]
			: false;

		return {
			label,
			value,
			disabled: isDisabled,
			disabledText: isDisabled ? requirement?.disabledText : undefined,
		};
	}

	function createSectionHeader(section: Pick<SelectboxOption, 'label' | 'value' | 'icon' | 'info'>): SelectboxOption {
		return {
			...section,
			disabled: true,
			sectionHeader: true,
		};
	}

	const groupedOptions = computed(() => ({
		hypermarkets: groupedBranches.value.hypermarkets.map((hm) => createBranchOption(hm.content.name, hm.slug)),
		fresh: groupedBranches.value.fresh.map((hm) => createBranchOption(hm.content.name, hm.slug)),
	}));

	const filterOptions = (options: BranchOption[]) => {
		if (!searchFormModel.value) {
			return options;
		}

		return options.filter((item) => localeIncludes(
			item.label,
			searchFormModel.value,
			{sensitivity: 'base'},
		));
	};

	const visibleSections = computed(() => {
		const sections = [
			{
				value: 'hypermarkets',
				label: 'Hypermarkety',
				icon: 'custom/house',
				options: filterOptions(groupedOptions.value.hypermarkets),
			},
			{
				value: 'fresh-stores',
				label: 'Prodejny Fresh',
				icon: 'custom/house',
				options: filterOptions(groupedOptions.value.fresh),
				info: {
					title: 'Co je fresh?',
					tooltip: 'Fresh je malý formát našich prodejen, kde naleznete specifické zboží pro danou lokalitu.',
				},
			},
		];

		if (hypermarketActiveType.value === 'all') {
			return sections.filter((section) => section.options.length);
		}

		return sections.filter((section) => section.value === hypermarketActiveType.value && section.options.length);
	});

	const flatDisplayedResults = computed<SelectboxOption[]>(() => visibleSections.value.flatMap((section) => [
		createSectionHeader({
			label: section.label,
			value: `__section-${section.value}`,
			icon: section.icon,
			info: section.info,
		}),
		...section.options,
	]));
	const hasVisibleBranchOptions = computed(() => visibleSections.value.some((section) => section.options.length > 0));
	const hasLoadedBranches = computed(() => hypermarkets.value !== null || freshBranches.value !== null);
	const showCategoryFilter = computed(() => !props.hideCategoryFilter && (freshBranches.value?.length ?? 0) > 0);

	const shoppingCartStore = useShoppingCartStore();
	const {items} = storeToRefs(shoppingCartStore);

	const warning = computed(() => (
		items.value.length
			? 'Pokud změníte pobočku dojde k odstranění produktů z košíku online objednávky.'
			: undefined
	));

	const searchFormModel = ref('');

	const dropdownPlaceholder = computed(() => selectedHypermarketWithFallbackInstance.value?.content.name);

	const selectedOption = ref(selectedHypermarket.value);
	const previousHypermarket = ref(selectedHypermarket.value);

	function changeHypermarket(hm?: string) {
		const newOption = hm || selectedOption.value;
		const nextBranchOption = flatDisplayedResults.value.find((option) => !option.sectionHeader && option.value === newOption);

		if (!newOption || nextBranchOption?.disabled) {
			return;
		}

		gtm?.push({
			event: 'branch_change',
			old: previousHypermarket.value,
			new: newOption,
		});

		hypermarketsStore.setHypermarket(newOption);
		previousHypermarket.value = newOption;

		emit('update:hm');

		if (props.showLinks && !mdAndLarger.value) {
			navigateToHypermarketLink(newOption);
			return;
		}

		if (props.showLinks) {
			return;
		}

		if ('branch' in route.params) {
			const target = branchSpecificPath({
				name: route.name,
				params: {
					...route.params,
					branch: newOption,
				},
				query: route.query,
				hash: route.hash,
			}).value;

			navigateTo(target);
		}
	}

	function getHypermarketLink(hm: string) {
		return props.redirectToBranch
			? {name: 'branch', params: {branch: hm}}
			: ('branch' in route.params)
				? branchSpecificPath({
					name: route.name,
					params: {
						...route.params,
						branch: hm,
					},
					query: route.query,
					hash: route.hash,
				})
				: {route};
	}

	function navigateToHypermarketLink(hm: string) {
		const target = getHypermarketLink(hm);
		const resolvedTarget = isRef(target) ? target.value : target;

		navigateTo(resolvedTarget);
	}

	function handelRedirect(hm: string) {
		if (props.showLinks && !mdAndLarger.value) {
			navigateToHypermarketLink(hm);
		}
	}

	const handleSameOption = (node: any) => node.on('sameOption', () => {
		handelRedirect(node.value);
	});

	watch(selectedOption, (newValue, oldValue) => {
		if (newValue && newValue !== oldValue) {
			changeHypermarket(newValue);
		}
	});

	watch(selectedHypermarket, (newValue) => {
		selectedOption.value = newValue;
	});

	onMounted(() => {
		gtm?.push({
			event: 'hyperselector_impression',
			selected_hypermarket: selectedHypermarketInstance.value?.content.name,
			content_hypermarket: visitedHypermarketInstance.value?.content.name,
			page_type: getGtmPageType(),
		});
	});
</script>
